import { lazy, Suspense, useEffect, useState } from 'react';
import type { Mode } from '@/state/store';
import { LoaderStage } from '@/features/loader/ui/LoaderStage';
import { TwinHud } from '@/features/twin/ui';
import { prewarmEngines } from './prewarmEngines';
import { cn } from '@/lib/cn';
import { PassContextBar } from '@/features/loader/passes/PassPlayback';

const TwinStage = lazy(() =>
  import('@/features/twin/scene/TwinStage').then((m) => ({ default: m.TwinStage })),
);
const OverviewStage = lazy(() =>
  import('@/features/overview/OverviewStage').then((m) => ({ default: m.OverviewStage })),
);
type Engine = 'overview' | 'globe' | 'twin';
const engineFor = (mode: Mode): Engine =>
  mode === 'overview' ? 'overview' : mode === 'maintenance' ? 'twin' : 'globe';

/** A visited engine keeps its camera and GPU assets; parked engines stop rendering.
 * Visibility preserves canvas dimensions and inert excludes hidden controls. */
export function CenterStage({ mode }: { mode: Mode }) {
  const active = engineFor(mode);
  const [visited, setVisited] = useState<Engine[]>([active]);
  if (!visited.includes(active)) setVisited([...visited, active]);
  useEffect(prewarmEngines, []);
  return (
    <div className="relative h-full w-full">
      {visited.map((engine) => {
        const parked = engine !== active;
        return (
          <div
            key={engine}
            data-engine={engine}
            data-active={!parked}
            inert={parked}
            aria-hidden={parked}
            className={cn(
              'absolute inset-0 transition-opacity duration-200 motion-reduce:transition-none',
              parked ? 'invisible pointer-events-none opacity-0' : 'visible opacity-100',
            )}
          >
            <Suspense fallback={<div className="absolute inset-0 bg-canvas" aria-label="Preparing scene" />}>
              {engine === 'overview' ? (
                <OverviewStage parked={parked} />
              ) : engine === 'globe' ? (
                <LoaderStage parked={parked} mode={mode} />
              ) : (
                <>
                  <TwinStage parked={parked} />
                  {!parked && <TwinHud />}
                  {!parked && <PassContextBar />}
                </>
              )}
            </Suspense>
          </div>
        );
      })}
    </div>
  );
}
